import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Table, PagingQuery } from 'tea-component';
import { useIntl, useHistory, getLocale } from 'umi';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import { getCountryFlag } from '@/pages/home/_config/countries';
import { TopchartTableProps } from '@/pages/intelligence/pages/_config/topchartsInterface';
import FirstRow from '@/pages/intelligence/components/TableComponents/components/FirstRow';
import BubbleClose from '@/pages/intelligence/components/BubbleClose';
import {
  ActiveUserMetricColumn,
  dauTitle,
  revenueTitle,
  downloadTitle,
  arpuTitle,
} from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/ActiveUsersCloums';
import { buriedBtnPoint } from '@/pages/home/utils/buriedPoint';
import { dbiBtnLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import Databarin from '@/pages/intelligence/assets/svg/dataSource/databarin.svg';
import DatabrainBubbleTip from '@/pages/intelligence/components/TableComponents/components/FirstRow/DatabrainBubbleTip';
import TopchartTable from '@/pages/intelligence/components/TableComponents/TopchartTable';
import {
  TableCheckboxHeader,
  TableCheckboxRender,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import { getDetailGamePathCommon } from '@/pages/intelligence/utils/topchartsUtils';
import CommonColumns, {
  DetailsCloums,
} from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/CommonCloums';
import { valueFormat, valueFormatArpu, growthFormat } from '@/pages/intelligence/utils/commonUtils';
import { LANGUAGE_CN } from '@/pages/home/_config/config';
import { getGameRankDetails } from '@intelligence/utils/tableUtils';

interface ActiveUsersTableProps extends TopchartTableProps {
  showFirstGuide: boolean;
  selectDataSources: string[];
  dateType: string;
  setMetricCloumsIndex?: any;
  topChartsType: 'activeuser' | 'revenue' | 'downloads' | 'databrain';
  genreSource: 'iegg' | 'annie';
  entityType?: string;
  onClick?: (url: string) => Promise<void>;
}
export default function ActiveUsers(props: ActiveUsersTableProps) {
  const {
    showFirstGuide,
    loading,
    records = [],
    tableTotal = 0,
    pageSortAttr,
    tableSelection,
    tableSelectionAll,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
    selectDataSources,
    topChartsType,
    dateType,
    genreSource = 'iegg',
    entityType = '',
    onClick,
  } = props;

  const { formatMessage } = useIntl(); // 中英切换方法
  const locale = getLocale();

  const {
    tablePageIndex,
    tablePageSize,
    currentSortValue,
    currentSortMetric,
    currentRatioType,
    currentOrderSource,
  } = pageSortAttr || {};
  const history = useHistory();
  // const defaultPageSize = 100;
  const defaultPageSize = 50;
  const [pageIndex, setPageIndex] = useState(tablePageIndex || 1);
  const [pageSize, setPageSize] = useState(tablePageSize || defaultPageSize);
  const [selectList, setSelectList] = useState(tableSelection);
  const [bubbleVisible, setBubbleVisible] = useState(false);

  useEffect(() => {
    setPageIndex(tablePageIndex);
    setPageSize(tablePageSize);
  }, [tablePageIndex, tablePageSize]);

  const onPagingChange = (e: PagingQuery) => {
    setPageIndex(e.pageIndex || 1);
    setPageSize(e.pageSize || defaultPageSize);
    onTablePagingChange(e);
  };

  const onSortChage = (e: SortByItemType) => {
    onTableSortChage(e);
  };

  useEffect(() => {
    setSelectList(tableSelection);
  }, [tableSelection]);

  const getMetricValueOfCvm = (cvm: any, sortMetric: string, dataSource: String) => {
    const metrics = cvm?.metric_list || [];
    const metric = metrics.find((item: any) => {
      return item?.source === dataSource && item?.metric === sortMetric;
    });
    const value =
      sortMetric === 'arpu' ? valueFormatArpu(metric?.value, false, 0) : valueFormat(metric?.value);
    const growthDataObj = growthFormat({
      value: metric?.growth_ratio,
      valueType: 'pointValue',
      hasPoint: 'lessThanOne',
      fixedNum: 2,
      maxNumber: 99999,
    });
    return { value, growthDataObj };
  };

  const getMetricCloums = (dataSource: string[]) => {
    const metricCloums: any[] = [];
    // DAU Revenue Download 每个数据源赋值一遍
    const titles = [];
    if (topChartsType === 'activeuser') {
      titles.push(dauTitle(dateType), revenueTitle, arpuTitle, downloadTitle);
    } else if (topChartsType === 'databrain') {
      titles.push(dauTitle(dateType), revenueTitle, downloadTitle);
    } else if (topChartsType === 'revenue') {
      titles.push(revenueTitle);
      titles.push(arpuTitle);
    } else if (topChartsType === 'downloads') {
      titles.push(downloadTitle);
    }
    // console.log(titles);
    // console.log(revenueTitle, arpuTitle)

    titles.forEach((t) => {
      dataSource.forEach((source) => {
        metricCloums.push(
          ActiveUserMetricColumn({
            dataSource: source, // source === 'ogdb' ? 'databarin' : source,
            title: locale === LANGUAGE_CN ? t?.title_cn || t?.title : t?.title,
            title_cn: t?.title_cn,
            title_en: t?.title,
            textContent: t.textContent,
            key: `${t.key}_${source}`,
            sortMetric: t.key,
            metricKey: t?.metricKey || t.key,
            currentSortValue,
            currentSortKey: `${currentSortMetric}_${currentOrderSource}_${currentRatioType}`,
            onSortChage,
            width: t?.width,
            width_cn: t?.width_cn,
            getMetricValueOfCvm,
            entityType,
          }),
        );
      });
    });
    return metricCloums;
  };

  useEffect(() => {
    setBubbleVisible(showFirstGuide && !loading && records.length > 0);
  }, [showFirstGuide, loading, records.length]);

  const onBubbleClose = () => {
    // 表格关注提示关闭打点
    buriedBtnPoint(dbiBtnLogMes.topchartMobileGameActiveUserTableTip);
    setBubbleVisible(false);
  };

  const getDetailGamePath = (gameId: string) => {
    return getDetailGamePathCommon({
      entityType: 'mobile',
      gameId,
      tab: 'stats',
      subTab: 'activeusers',
    });
  };

  const onClickGame = (gameId: string) => {
    const url = getDetailGamePath(gameId);

    if (onClick) {
      onClick(url);
      return;
    }

    history.push(url);
  };

  return (
    <>
      <TopchartTable
        loading={loading}
        recordKey={'id'}
        records={records}
        tableTotal={tableTotal}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPagingChange={onPagingChange}
        columns={[
          // { key: 'select', header: null, width: 37, fixed: 'left' },
          {
            key: 'select',
            width: 36,
            fixed: 'left',
            header: () => {
              return (
                <TableCheckboxHeader
                  selectList={selectList}
                  tableSelectionAll={tableSelectionAll}
                  disabled={loading || records.length === 0}
                  onChange={(list, stats) => {
                    onTableCheckGame(list, stats);
                    setSelectList(list);
                  }}
                />
              );
            },
            render: (cvm: any) => {
              return (
                <TableCheckboxRender
                  selectList={selectList}
                  cvm={{ ...(cvm || {}), gameId: cvm?.id }}
                  disabled={getGameRankDetails(cvm?.rank).isDisabled}
                  onChange={(list, status) => {
                    setSelectList(list);
                    onTableCheckGame(list, status);
                  }}
                />
              );
            },
          },
          {
            key: 'gametitle',
            header: (
              <BubbleClose
                style={{ width: '400px' }}
                placement="bottom-start"
                placementOffset={[60, -35]}
                content={formatMessage({ id: `dbi_first_follow_info` })}
                visible={bubbleVisible}
                onClose={onBubbleClose}
              >
                <div className="dbi-firstRow-flex">
                  <div className="dbi-text-id">#</div>
                  <div>{formatMessage({ id: 'dbi_top_gametitle' })}</div>
                </div>
              </BubbleClose>
            ),
            width: 330,
            fixed: 'left',
            render: (cvm: any) => (
              <FirstRow
                id={getGameRankDetails(cvm?.rank)?.rankText}
                cvm={{
                  img: cvm?.cover,
                  gametitle: cvm?.name,
                  countryIcon: getCountryFlag(cvm?.publisher_addr),
                  text: cvm?.publisher,
                  isDatabrain: cvm.b_ogdb_data && topChartsType !== 'databrain' ? Databarin : '',
                  databrainIconTip: <DatabrainBubbleTip />,
                }}
                rihgtClickToJumpHref={getDetailGamePath(cvm?.id)}
                onClick={() => {
                  onClickGame(cvm?.id);
                }}
              />
            ),
          },
          ...getMetricCloums(selectDataSources),
          CommonColumns.ReleaseTimeColumn({ locale, formatMessage }),
          CommonColumns.GenreColumn({
            genreSource: topChartsType === 'databrain' ? '' : genreSource,
            mainGenreField: 'genre',
            subGenreField: 'sub_genre',
          }),
          DetailsCloums({
            onClickGame: (cvm: any) => {
              onClickGame(cvm?.id);
            },
            formatMessage,
          }),
        ]}
      />
    </>
  );
}
